import { useRef } from "react";
import { QRCodeSVG } from "qrcode.react";
import { twMerge } from "tailwind-merge";

import {
  ArrowRepeatIcon,
  Button,
  CalendarIcon,
  CardInputsContainer,
  DollarIcon,
  UserCircleIcon,
} from "~/common/components";
import { BUTTON_VARIANTS } from "~/common/constants";
import { InvitationLink } from "~/common/types/invitationLink";
import { copyToClipboard } from "~/common/utils";
import { hasAdminAccess } from "~/common/utils/checkUserPermissions";
import { handleDownloadSvg } from "~/common/utils/handleDownloadSvg";
import {
  useCurrentTeamMemberContext,
  useIsTeamMemberStaff,
} from "~/providers/hooks";
import { OrganizationProvidersResponse } from "~/providers/types/teamMembers";
import { generateInvitationLink } from "~/providers/utils/invitation/generateInvitationLink";
import { ActivateDeactivateConsultationLinkModal } from "./ActivateDeactivateConsultationLinkModal";
import { DeleteConsultationLinkModal } from "./DeleteConsultationLinkModal";
import { UpdateConsultationLinkModal } from "./UpdateConsultationLinkModal";

interface ConsultationRequestLinkProps {
  link: InvitationLink;
  isDeleteEnabled?: boolean;
  isReadOnly?: boolean;
  providers: OrganizationProvidersResponse[];
}

export const ConsultationRequestLink = ({
  link,
  isDeleteEnabled = true,
  isReadOnly = false,
  providers,
}: ConsultationRequestLinkProps) => {
  const teamMember = useCurrentTeamMemberContext();
  const { isStaff } = useIsTeamMemberStaff();
  const svgRef = useRef<SVGSVGElement>(null);

  const invitationURL = generateInvitationLink(link.code);
  const providerFullName =
    link.default_primary_provider?.full_name ?? "Unassigned";

  const cardInformationData = [
    {
      icon: <UserCircleIcon className="size-[18px]" />,
      title: "Primary provider",
      info: providerFullName,
    },
    {
      icon: <DollarIcon className="size-[18px]" />,
      title: "Default fee",
      info: `$${link.default_consultation_fee}`,
    },
    {
      icon: <ArrowRepeatIcon className="size-[18px]" />,
      title: "Auto promote to intake",
      info: link.auto_promote ? "On" : "Off",
    },
    {
      icon: <CalendarIcon className="size-[18px]" />,
      title: "Scheduling included",
      info: link.scheduling_included ? "On" : "Off",
    },
  ];

  return (
    <div className={twMerge(!link.is_active && "grayscale")}>
      <CardInputsContainer
        className="md:max-w-none"
        title={link.label}
        actions={
          !isReadOnly &&
          hasAdminAccess(teamMember?.user_permissions) && (
            <div className="flex items-center gap-2">
              <ActivateDeactivateConsultationLinkModal link={link} />
              <div
                aria-hidden={true}
                className="block w-[1px] self-stretch bg-neutral-80"
              ></div>
              <UpdateConsultationLinkModal link={link} providers={providers} />
              {!isStaff && (
                <DeleteConsultationLinkModal
                  linkId={link.id}
                  isDeleteEnabled={isDeleteEnabled}
                />
              )}
            </div>
          )
        }
      >
        <div className="flex flex-col gap-4">
          {link.description && <p>{link.description}</p>}
          <div className="flex flex-col items-center gap-3 sm:flex-row">
            <div className="flex flex-col items-center gap-4 lg:flex-row">
              <QRCodeSVG size={100} ref={svgRef} value={invitationURL} />

              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-2">
                  <Button
                    className="whitespace-nowrap"
                    onClick={() => copyToClipboard(invitationURL)}
                    disabled={!link.is_active}
                  >
                    Copy link (URL)
                  </Button>
                  <Button
                    className="whitespace-nowrap px-3"
                    variant={BUTTON_VARIANTS.outline_black}
                    onClick={() => handleDownloadSvg(svgRef, "qrcode.svg")}
                    disabled={!link.is_active}
                  >
                    Download QR code
                  </Button>
                </div>
              </div>
            </div>

            <div
              aria-hidden={true}
              className="mx-0 my-4 block h-[1px] w-full self-stretch bg-neutral-80 sm:mx-2 sm:my-0 sm:h-auto sm:w-[1px]"
            ></div>

            <div className="flex w-full flex-1 flex-col gap-3 sm:w-auto">
              {cardInformationData.map((option) => (
                <span
                  key={option.title}
                  className="flex flex-nowrap items-center gap-1"
                >
                  <div className="flex items-center gap-1.5">
                    {option.icon}
                    <span className="whitespace-nowrap">{option.title}</span>
                  </div>
                  <span className="rounded-full bg-neutral-5 px-2.5 py-[2px]">
                    {option.info}
                  </span>
                </span>
              ))}
            </div>

            <div
              aria-hidden={true}
              className="mx-0 block h-[1px] w-full self-stretch bg-neutral-80 sm:mx-2 sm:h-auto sm:w-[1px]"
            ></div>

            <div className="flex w-full flex-col gap-1 sm:w-auto">
              <span className="font-outfit text-4xl">
                {link.sourced_count ?? 0}
              </span>
              <span className="max-w-24">
                {link.sourced_count === 1 ? "Consultation" : "Consultations"}{" "}
                sourced
              </span>
            </div>
          </div>
        </div>
      </CardInputsContainer>
    </div>
  );
};
